import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Menu, X, User, LogOut, Wallet, Shield, LayoutDashboard, Globe } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Public homepage keeps ONLY: Relaxora ME · EN · Login · Join.
  const isHomepage = location.pathname === "/";

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashPath = user
    ? user.role === "admin" || user.role === "moderator"
      ? "/admin/dashboard"
      : user.role === "agency"
      ? "/dashboard/agency"
      : user.role === "independent"
      ? "/dashboard/independent"
      : "/dashboard/client"
    : "/login";

  return (
    <header className="sticky top-0 z-40 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <span className="font-heading text-2xl tracking-wide text-white">Relaxora</span>
          <span className="text-[10px] text-gold tracking-[0.3em] font-semibold mt-1">ME</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {!isHomepage && (
            <>
              <Link to="/" className="text-[#a3a3a3] hover:text-white transition-colors" data-testid="nav-home">{t.nav.home}</Link>
              <Link to="/directory" className="text-[#a3a3a3] hover:text-white transition-colors" data-testid="nav-directory">{t.nav.directory}</Link>
              <Link to="/agencies" className="text-[#a3a3a3] hover:text-white transition-colors" data-testid="nav-agencies">{t.nav.agencies}</Link>
              {user && (
                <Link to="/wallet" className="text-[#a3a3a3] hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-wallet">
                  <Wallet className="w-4 h-4" strokeWidth={1.5} /> {t.nav.wallet}
                  <span className="ml-1 text-gold text-xs">{user.credits ?? 0}</span>
                </Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs">
            <button
              onClick={() => setLang(lang === "en" ? "ru" : "en")}
              className="px-2 py-1 rounded border border-white/10 text-[#a3a3a3] hover:text-white hover:border-gold/50 transition-colors flex items-center gap-1.5"
              data-testid="lang-toggle"
            >
              <Globe className="w-3 h-3" strokeWidth={1.5} />
              {lang.toUpperCase()}
            </button>
          </div>

          {!user ? (
            <>
              <Link to="/login" className="text-sm text-[#a3a3a3] hover:text-white" data-testid="cta-login">
                {t.nav.login}
              </Link>
              <Link to="/register" className="btn-gold rounded-md px-3 sm:px-4 py-2 text-sm" data-testid="cta-register">
                {t.nav.register}
              </Link>
            </>
          ) : (
            <div className="relative">
              <button
                onClick={() => setMenu((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded border border-white/10 hover:border-gold/50 transition-colors"
                data-testid="user-menu-btn"
              >
                <User className="w-4 h-4 text-gold" strokeWidth={1.5} />
                <span className="text-sm text-white hidden sm:inline">{user.name}</span>
              </button>
              {menu && (
                <div className="absolute right-0 top-12 w-56 bg-[#121212] border border-white/10 rounded-md p-2 shadow-2xl" data-testid="user-menu">
                  <Link to={dashPath} onClick={() => setMenu(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-[#e5e5e5] hover:bg-white/5 rounded" data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {t.nav.dashboard}
                  </Link>
                  <Link to="/wallet" onClick={() => setMenu(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-[#e5e5e5] hover:bg-white/5 rounded" data-testid="menu-wallet">
                    <Wallet className="w-4 h-4" strokeWidth={1.5} /> {t.nav.wallet}
                  </Link>
                  <Link to="/verification" onClick={() => setMenu(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-[#e5e5e5] hover:bg-white/5 rounded" data-testid="menu-verification">
                    <Shield className="w-4 h-4" strokeWidth={1.5} /> {t.nav.verification}
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin/dashboard" onClick={() => setMenu(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-[#e5e5e5] hover:bg-white/5 rounded" data-testid="menu-admin">
                      <Shield className="w-4 h-4" strokeWidth={1.5} /> {t.nav.admin}
                    </Link>
                  )}
                  <div className="h-px bg-white/5 my-1" />
                  <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#e5e5e5] hover:bg-white/5 rounded" data-testid="menu-logout">
                    <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t.nav.logout}
                  </button>
                </div>
              )}
            </div>
          )}

          <button className={`md:hidden text-white ${isHomepage ? "hidden" : ""}`} onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 px-4 py-4 space-y-3 bg-[#0a0a0a]" data-testid="mobile-menu">
          <Link to="/" onClick={() => setOpen(false)} className="block text-[#a3a3a3] text-sm py-1">{t.nav.home}</Link>
          <Link to="/directory" onClick={() => setOpen(false)} className="block text-[#a3a3a3] text-sm py-1">{t.nav.directory}</Link>
          <Link to="/agencies" onClick={() => setOpen(false)} className="block text-[#a3a3a3] text-sm py-1">{t.nav.agencies}</Link>
          {user && <Link to="/wallet" onClick={() => setOpen(false)} className="block text-[#a3a3a3] text-sm py-1">{t.nav.wallet}</Link>}
        </div>
      )}
    </header>
  );
}
